import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";


let scene, camera, controls, renderer, textureLoader, container;

const blenderMaterials = {
  lampshade: null,
  lampBase: null,
  trimTop: null,
  trimBottom: null
};

const fabricTextures = {};

export async function initLampScene() {

  container = document.getElementById("lamp-3d-viewer");

  if (!container) {
    throw new Error("Lamp container not found");
  } 

  initThreeJS();
  preloadFabrics();

  await loadBlenderModel();
  
  animate();

  return true;
}

function initThreeJS() {
  scene = new THREE.Scene();
  scene.background = new THREE.Color(0xECECEC);
  textureLoader = new THREE.TextureLoader();

  camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);

  renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      physicallyCorrectLights: true
    });

  renderer.outputColorSpace = THREE.SRGBColorSpace;

  container.appendChild(renderer.domElement);

  scene.traverse(obj => {
    if (obj.isLight) scene.remove(obj);
  });

  const keyLight = new THREE.DirectionalLight(0xfff9d6, 0.43);
  keyLight.position.set(2, 1, 3);
  keyLight.target.position.set(0, 1.25, 0)
  scene.add(keyLight);
  scene.add(keyLight.target)

  const keyLight2 = new THREE.DirectionalLight(0xfff9d6, 0.43);
  keyLight2.position.set(1, 1, -2);
  scene.add(keyLight2);

  const ambient = new THREE.AmbientLight(0xffffff, 0.63);
  scene.add(ambient);

  // Controls
  controls = new OrbitControls(camera, renderer.domElement);

  // Enable smooth interaction
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;

  // Allow panning
  controls.enablePan = false;

  // Optional limits (nice for product viewers)
  controls.minDistance = 0.4;
  controls.maxDistance = 1;
  controls.enableZoom = true;

  controls.minPolarAngle = Math.PI / 3;
  controls.maxPolarAngle = Math.PI * 0.75;

  resizeRenderer();

  window.addEventListener("resize", resizeRenderer);
}

function resizeRenderer() {
  const width = container.offsetWidth;
  const height = container.offsetHeight || 500;

  if (width === 0) return;

  renderer.setSize(width, height);

  camera.aspect = width / height;
  camera.updateProjectionMatrix();
}

function loadBlenderModel() {
    return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();

    loader.load(
      "https://cdn.shopify.com/3d/models/8cc7cf8f47ef051d/TestLamp20.glb",
      function (gltf) {
        const model = gltf.scene;
        scene.add(model);

        // Auto center + scale
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3()).length();

        model.position.sub(center);

        camera.position.set(0, 0.08, size * 1.25);
        camera.lookAt(0, 0, 0);

        model.traverse((child) => {
          if (!child.isMesh) return;

          child.castShadow = true;
          child.receiveShadow = true;

          const mat = child.material;

          if (!mat) return;

          switch (mat.name) {
            case "LampBaseMaterial":
              blenderMaterials.lampBase = mat;
              blenderMaterials.lampBase.roughness = 0.2;
              blenderMaterials.lampBase.clearcoat = 0.3;
              blenderMaterials.lampBase.clearcoatRoughness = 0.1;
              break;

            case "TrimTopMaterial":
              blenderMaterials.trimTop = mat;
              break;

            case "TrimBottomMaterial":
              blenderMaterials.trimBottom = mat;
              break;

            case "FabricMaterial":
              blenderMaterials.lampshade = mat;
              blenderMaterials.lampshade.roughness = 0.7;
              break;

            case "PVCFolieMaterial":  
              mat.color.set(0xca9e20)
              mat.roughness = 0.7;
              mat.metalness = 0.3; 
              break;

            case "ShinyGold":
              mat.color.set(0xca9e20)
              mat.emissive = new THREE.Color(0xb06b00); // White glow
              mat.emissiveIntensity = 0.2;
              mat.roughness = 0.1;
              mat.metalness = 0.6; 
              break;
          }
        });

        console.log("Blender model loaded successfully");
        resolve();
      },
      undefined,
      (error) => {
        console.error("Error loading Blender model:", error);
        reject(error)
      }
    );
  });
}

function animate() {
  requestAnimationFrame(animate);

  if (controls) controls.update(); // required for damping

  if (renderer && scene && camera) {
    renderer.render(scene, camera);
  }
}

function preloadFabrics() {
  const fabricList = [
    { id: "Dazzling Diamond", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Dazzling-diamond-fabric.jpg?v=1772641915" },
    { id: "Midnight Maze", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Midnight-maze-fabric.jpg?v=1772633345" },
    { id: "Boho Glow", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Boho-glow-fabric.jpg?v=1772641907" },
    { id: "Boho Glow 2", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Boho-Glow-II-fabric.jpg?v=1772641749" },
    { id: "Mystic Maya", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Mystic-maya-fabric.jpg?v=1772640855" },
    { id: "Tamil Nadu", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Tamil-nadu-fabric.jpg?v=1772641755" },
    { id: "Ziggy Pop", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Ziggy-pop-fabric.jpg?v=1773085895" },
    { id: "Summer Pop", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Summer-pop-fabric.jpg?v=1774210835" },
    { id: "Red Revel", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Red-revel-fabric.jpg?v=1774271507" },
    { id: "Flourish Fuchsia", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Flourish-fuchsia-fabric.jpg?v=1774272611" },
    { id: "Mango Tango", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Mango-tango-fabric.jpg?v=1774271936" },
    { id: "Indian Garden", url: "https://cdn.shopify.com/s/files/1/0853/4343/7137/files/Indian-garden-fabric.jpg?v=1774364939" },
  ];

  fabricList.forEach(fabric => {
    textureLoader.load(fabric.url, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;

      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;

      texture.repeat.set(-4, 4.8); // minus: to flip
      texture.rotation = Math.PI*-0.5;

      fabricTextures[fabric.id] = texture;
    });
  });
}

/* ---------- Update Lamp Model ---------- */
export function updateLampModel(config) {
  if (!config) return;

  // Lamp base color
  if (blenderMaterials.lampBase && config.lampBaseColor) {
    blenderMaterials.lampBase.color.set(config.lampBaseColor);
  }

  // Trim color (top + bottom)
  if (config.trimColor) {
    if (blenderMaterials.trimTop) {
      blenderMaterials.trimTop.color.set(config.trimColor);
    }

    if (blenderMaterials.trimBottom) {
      blenderMaterials.trimBottom.color.set(config.trimColor);
    }
  }  

  if (blenderMaterials.lampshade && config.fabric) {
    const texture = fabricTextures[config.fabric];
    
    if (!texture) {
      console.warn("Fabric not loaded:", config.fabric);
      return;
    }
    
    blenderMaterials.lampshade.map = texture;
    blenderMaterials.lampshade.needsUpdate = true;
  }
}